import cn from 'classnames';
import { Link } from 'react-router-dom';

import { OverlayCard } from 'components/Cards/OverlayCards';
import { UserInfo } from 'components/Data/UserInfo';

import styles from './../components/HistoryRow/HistoryRow.module.scss';
import { CheckHistoryTypes } from './check-history-types';

/**
 * Each action is defined as a function that takes a data object.
 * This data object contains all the values required to generate the icon and description.
 *
 */
const icons = {
  customNote: 'edit',
  customInfo: 'info',
  customSuccess: 'check-circle',
  customWarning: 'alert-triangle',
  customError: 'alert-circle',
};

const createCustomAction =
  type =>
  ({ userName, entry, companyUsers, checkHuman, styles }) => ({
    icon: icons[type],
    description: (
      <p>
        {userName}:
        <span className={cn(styles.wrap, 'd-flex flex-wrap', 'u-padding-left--small')}>
          {entry?.message?.split(/(@\[.*?\]\(\d+\))/).map((part, index, array) => {
            const match = part.match(/@\[(.*?)\]\((\d+)\)/);
            if (match) {
              const [_, name, id] = match;
              const userInfo = companyUsers?.find(user => user.id === Number(id));
              return userInfo ? (
                <OverlayCard name={name} key={`${id}-${index}`} className={styles.userLink}>
                  <UserInfo
                    display={`${userInfo.first_name} ${userInfo.last_name}`}
                    email={userInfo.email}
                    firstName={userInfo.first_name}
                    lastName={userInfo.last_name}
                  />
                </OverlayCard>
              ) : (
                <span key={`${id}-${index}`}>{name}</span>
              );
            }
            return part;
          })}
        </span>
        {entry?.check_type && (
          <span>
            . <b className={styles.userRef}>[{checkHuman}]</b>
          </span>
        )}
      </p>
    ),
  });

export const checkHistoryActions = {
  [CheckHistoryTypes.CompletedCheck]: ({ refCandidate, referenceLink }) => ({
    icon: 'check-circle',
    description: (
      <p>
        Your check is completed for {refCandidate}. {referenceLink}
      </p>
    ),
  }),
  [CheckHistoryTypes.CompletedReferee]: ({ refReferee, country, referenceLink }) => ({
    icon: 'check-circle',
    description: (
      <p>
        Reference check completed by {refReferee} {country}. {referenceLink}
      </p>
    ),
  }),
  [CheckHistoryTypes.CompletedRefereeIpError]: ({
    refReferee,
    country,
    refCandidate,
    referenceLink,
  }) => ({
    icon: 'alert-triangle',
    description: (
      <p>
        Reference check completed by {refReferee} {country} using the same IP address as the
        candidate {refCandidate}. {referenceLink}
      </p>
    ),
  }),
  [CheckHistoryTypes.CompletedCandidate]: ({
    refCandidate,
    candidateRefereeLinks,
    country,
    referees,
  }) =>
    referees && referees.length > 0
      ? {
          icon: 'check-circle',
          description: (
            <p>
              Candidate {refCandidate} successfully provided referee details for{' '}
              <span>{candidateRefereeLinks}</span> {country}
            </p>
          ),
        }
      : {
          icon: 'check-circle',
          description: (
            <p>
              Candidate {refCandidate} successfully provided referee details. {country}
            </p>
          ),
        },
  [CheckHistoryTypes.UpdatedEmail]: ({ userName, refCandidate, refReferee, referee }) => ({
    icon: 'mail',
    description: (
      <p>
        {userName} updated {referee ? refReferee : refCandidate}'s email.
      </p>
    ),
  }),
  [CheckHistoryTypes.UpdatedContactNumber]: ({ userName, refCandidate, refReferee, referee }) => ({
    icon: 'phone',
    description: (
      <p>
        {userName} updated {referee ? refReferee : refCandidate}'s phone number.
      </p>
    ),
  }),
  [CheckHistoryTypes.UpdatedDepartment]: ({ userName, refCandidate }) => ({
    icon: 'edit',
    description: (
      <p>
        {userName} updated {refCandidate}'s department information.
      </p>
    ),
  }),
  [CheckHistoryTypes.UpdatedJobRole]: ({ userName, refCandidate }) => ({
    icon: 'edit',
    description: (
      <p>
        {userName} updated {refCandidate}'s job title.
      </p>
    ),
  }),
  [CheckHistoryTypes.RequestSentCandidate]: ({ userName, refCandidate }) => ({
    icon: 'send',
    description: (
      <p>
        {userName} sent candidate {refCandidate} a new request.
      </p>
    ),
  }),
  [CheckHistoryTypes.RequestOverdueCandidate]: ({
    refCandidate,
    candidate,
    onCandidateRequest,
  }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        Check for candidate {refCandidate} overdue.{' '}
        {candidate.status === 'Candidate' && (
          <a onClick={() => onCandidateRequest()}>Resend request</a>
        )}
      </p>
    ),
  }),
  [CheckHistoryTypes.RequestSentReferee]: ({ userName, refReferee }) => ({
    icon: 'send',
    description: (
      <p>
        {userName} sent referee {refReferee} a new request.
      </p>
    ),
  }),
  [CheckHistoryTypes.RequestOverdueReferee]: ({ refReferee, referee, onRefereeRequest }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        Reference request for referee {refReferee} overdue.{' '}
        {referee && !referee.completed_at && (
          <a onClick={() => onRefereeRequest(referee.id)}>Resend request</a>
        )}
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateEmailFailed]: ({ refCandidate }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        Email couldn't be sent to {refCandidate}. You tried to send to a recipient that has been
        marked as inactive.
      </p>
    ),
  }),
  [CheckHistoryTypes.RefereeEmailFailed]: ({ refReferee }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        Email couldn't be sent to referee {refReferee}. You tried to send to a recipient that has
        been marked as inactive.
      </p>
    ),
  }),
  [CheckHistoryTypes.NewReferee]: ({ refCandidate, userName, refReferee, entry }) => ({
    icon: 'user-plus',
    description: (
      <p>
        {entry.user_name ? (
          <>
            {userName} added new referee {refReferee}
          </>
        ) : (
          <>
            {refCandidate} nominated {refReferee}
          </>
        )}
      </p>
    ),
  }),
  [CheckHistoryTypes.NewCheck]: ({
    userName,
    checkHuman,
    refCandidate,
    backgroundChecks,
    entry,
  }) => ({
    icon: 'check-circle',
    description: (
      <p>
        {userName} created a new {checkHuman} for candidate.{' '}
        {entry.check_type === CheckHistoryTypes.CheckReference ? refCandidate : backgroundChecks}
      </p>
    ),
  }),
  [CheckHistoryTypes.BackgroundCheckSubmitted]: ({ refCandidate }) => ({
    icon: 'check-circle',
    description: <p>{refCandidate} has completed the background application.</p>,
  }),
  [CheckHistoryTypes.BackgroundCheckProcessing]: ({
    refCandidate,
    checkHuman,
    backgroundChecks,
  }) => ({
    icon: 'loader',
    description: (
      <p>
        {refCandidate}'s {checkHuman} is being processed. {backgroundChecks}
      </p>
    ),
  }),
  [CheckHistoryTypes.BackgroundCheckCompleted]: ({
    refCandidate,
    checkHuman,
    backgroundChecks,
  }) => ({
    icon: 'check-circle',
    description: (
      <p>
        {refCandidate}'s {checkHuman} has been completed. {backgroundChecks}
      </p>
    ),
  }),
  [CheckHistoryTypes.BackgroundCheckRejected]: ({ checkHuman, refCandidate }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        {checkHuman} has been declined. New request sent to {refCandidate}.
      </p>
    ),
  }),
  [CheckHistoryTypes.BackgroundCheckUpdated]: ({ refCandidate, checkHuman, backgroundChecks }) => ({
    icon: 'refresh-ccw',
    description: (
      <p>
        {refCandidate} updated {checkHuman} declined fields. {backgroundChecks}
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckCanceled]: ({ userName, refCandidate, checkHuman, entry }) => ({
    icon: 'alert-circle',
    description: entry?.message ? (
      <p>
        <span className={styles.wrap}>{entry.message}</span>
      </p>
    ) : (
      <p>
        {userName} canceled {refCandidate}'s {checkHuman}.
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateSMSOpened]: ({ refCandidate }) => ({
    icon: 'info',
    description: <p>{refCandidate} has opened the SMS.</p>,
  }),
  [CheckHistoryTypes.RefereeSMSOpened]: ({ refReferee }) => ({
    icon: 'info',
    description: <p>{refReferee} has opened the SMS.</p>,
  }),
  [CheckHistoryTypes.CandidateEmailOpened]: ({ refCandidate }) => ({
    icon: 'info',
    description: <p>{refCandidate} has opened the email.</p>,
  }),
  [CheckHistoryTypes.RefereeEmailOpened]: ({ refReferee }) => ({
    icon: 'info',
    description: <p>{refReferee} has opened the email.</p>,
  }),
  [CheckHistoryTypes.CandidateEmailClicked]: ({ refCandidate }) => ({
    icon: 'info',
    description: <p>{refCandidate} has clicked a link in the email.</p>,
  }),
  [CheckHistoryTypes.RefereeEmailClicked]: ({ refReferee }) => ({
    icon: 'info',
    description: <p>{refReferee} has clicked a link in the email.</p>,
  }),
  [CheckHistoryTypes.CustomNote]: createCustomAction('customNote'),
  [CheckHistoryTypes.CustomInfo]: createCustomAction('customInfo'),
  [CheckHistoryTypes.CustomSuccess]: createCustomAction('customSuccess'),
  [CheckHistoryTypes.CustomWarning]: createCustomAction('customWarning'),
  [CheckHistoryTypes.CustomError]: createCustomAction('customError'),

  [CheckHistoryTypes.CheckDeclined]: ({ entry }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        Check declined with reason: "<span className={styles.wrap}>{entry.message}</span>"
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckAddNotes]: ({ refCandidate, entry }) => ({
    icon: 'info',
    description: (
      <p>
        Checkmate notes for {refCandidate}: "<span className={styles.wrap}>{entry.message}</span>"
      </p>
    ),
  }),
  [CheckHistoryTypes.SubCheckCompleted]: ({ refCandidate, checkHuman, backgroundChecks }) => ({
    icon: 'check-circle',
    description: (
      <p>
        {refCandidate}'s {checkHuman} has a new partial result. {backgroundChecks}
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckUpdateStatus]: ({ userName, checkHuman, entry }) => ({
    icon: 'info',
    description: (
      <p>
        {userName}{' '}
        <span className={styles.wrap}>
          marked the approval status of {checkHuman} as{' '}
          <span className="u-capitalize">"{entry.message?.replace('_', ' ')}"</span>
        </span>
      </p>
    ),
  }),
  [CheckHistoryTypes.SubCheckUpdateStatus]: ({ userName, checkHuman, entry }) => {
    const [subCheckName, status] = entry.message?.split('||');
    return {
      icon: 'info',
      description: (
        <p>
          {userName} marked the approval status of {subCheckName} from {checkHuman} as "
          {status?.replace('_', ' ')}"
        </p>
      ),
    };
  },
  [CheckHistoryTypes.RefereeDeleted]: ({ userName, entry }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        {userName} deleted referee{' '}
        <b className={cn(styles.userRef, 'u-padding-left--small')}>
          <span className={styles.wrap}>{entry.message}</span>
        </b>
        .
      </p>
    ),
  }),
  [CheckHistoryTypes.RefereeReplacement]: ({ userName, refCandidate, refReferee, entry }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        {userName} asked {refCandidate} to replace {refReferee}{' '}
        {entry.message && (
          <>
            with reason: "<span className={styles.wrap}>{entry.message}</span>"
          </>
        )}
        .
      </p>
    ),
  }),
  [CheckHistoryTypes.ReminderCandidate]: ({ userName, refCandidate, entry }) => {
    return {
      icon: 'info',
      description:
        entry?.user_name && entry?.user_name?.trim().toLowerCase() !== 'user' ? (
          <p>
            Reminder sent to candidate {refCandidate} by&nbsp;{userName}.
          </p>
        ) : (
          <p>Automated reminder sent to candidate {refCandidate}.</p>
        ),
    };
  },
  [CheckHistoryTypes.ReminderReferee]: ({ userName, refReferee, entry }) => ({
    icon: 'info',
    description:
      entry?.user_name && entry?.user_name?.trim().toLowerCase() !== 'user' ? (
        <p>
          Reminder sent to referee {refReferee} by&nbsp;{userName}.
        </p>
      ) : (
        <p>Automated reminder sent to referee {refReferee}.</p>
      ),
  }),
  [CheckHistoryTypes.UpdatedCandidateTeam]: ({ userName, refCandidate }) => ({
    icon: 'edit',
    description: (
      <p>
        {userName} updated {refCandidate}'s team.
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckResult]: ({ checkHuman, userName }) => ({
    icon: 'info',
    description: (
      <p>
        {checkHuman} report downloaded by <span style={{ paddingLeft: '3px' }}> {userName}</span>
      </p>
    ),
  }),
  [CheckHistoryTypes.TransferCandidateAssignment]: ({ userName, entry }) => ({
    icon: 'info',
    description: (
      <p>
        {userName} set {entry.message} as the assigned candidate owner.
      </p>
    ),
  }),
  [CheckHistoryTypes.NewCandidateAssignment]: ({ userName, entry }) => ({
    icon: 'info',
    description: (
      <p>
        {userName} assigned candidate to {entry.message}.
      </p>
    ),
  }),
  [CheckHistoryTypes.CheckUndoCompletion]: ({ userName, refCandidate, entry }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        {userName} undid the completion of {refCandidate}'s {entry.check_type} check.
      </p>
    ),
  }),
  [CheckHistoryTypes.NewReferencePlusForm]: ({ entry }) => {
    const [index, refereeFormName, candidateFormName] = entry?.message?.split('::');
    return {
      icon: 'info',
      description: entry?.message ? (
        <p>
          Referee #<b className={styles.userRef}>{index}&nbsp;</b> created for candidate with
          referee form{' '}
          <Link target="_blank" to={`/questionnaires/referees_reference`}>
            {refereeFormName}
          </Link>
          and candidate form{' '}
          <Link target="_blank" to={`/questionnaires/candidate_reference`}>
            {candidateFormName}
          </Link>
          .
        </p>
      ) : (
        <p> There was an error creating the history for the reference plus forms.</p>
      ),
    };
  },
  [CheckHistoryTypes.NewReferencePlusCommonForm]: ({ entry }) => {
    const [count, refereeFormName, candidateFormName] = entry?.message?.split('::');
    return {
      icon: 'info',
      description: entry?.message ? (
        <p>
          <b className={styles.userRef}>
            {count} {Number(count) > 1 ? 'referees' : 'referee'}&nbsp;
          </b>{' '}
          created for candidate with referee form{' '}
          <Link target="_blank" to={`/questionnaires/referees_reference`}>
            {refereeFormName}
          </Link>
          and candidate form{' '}
          <Link target="_blank" to={`/questionnaires/candidate_reference`}>
            {candidateFormName}
          </Link>
          .
        </p>
      ) : (
        <p> There was an error creating the history for the reference plus forms.</p>
      ),
    };
  },
  [CheckHistoryTypes.NewReferenceForm]: ({ entry }) => {
    const [count, refereeFormName] = entry?.message?.split('::');
    return {
      icon: 'info',
      description: entry?.message ? (
        <p>
          <b className={styles.userRef}>{count}&nbsp;</b> {count > 1 ? 'referees' : 'referee'}{' '}
          created for candidate with referee form{' '}
          <Link target="_blank" to={`/questionnaires/custom`}>
            {refereeFormName}
          </Link>
          .
        </p>
      ) : (
        <p> There was an error creating the history for the reference check form.</p>
      ),
    };
  },
  [CheckHistoryTypes.UserCompletedRefereeForm]: ({ userName, refCandidate, refReferee }) => ({
    icon: 'info',
    description: (
      <p>
        {userName} answered the referee form on behalf of {refReferee}.
      </p>
    ),
  }),
  [CheckHistoryTypes.BundleCheckAdded]: ({ refCandidate, entry }) => ({
    icon: 'info',
    description: (
      <p>
        Checks were added using the bundle{' '}
        <span className="u-font-weight-600 primary-text">&nbsp;{entry?.message}&nbsp;</span> for{' '}
        {refCandidate}.
      </p>
    ),
  }),
  [CheckHistoryTypes.UpdatedCandidateArchived]: ({ userName, refCandidate }) => ({
    icon: 'info',
    description: (
      <p>
        {refCandidate} was archived by&nbsp;{userName}.
      </p>
    ),
  }),
  [CheckHistoryTypes.RefereeRequestSent]: ({ userName, refCandidate }) => ({
    icon: 'send',
    description: (
      <p>
        {userName} asked {refCandidate} to nominate a new referee.
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateEmailDelivered]: ({ refCandidate }) => ({
    icon: 'info',
    description: <p>Email delivered to {refCandidate}.</p>,
  }),
  [CheckHistoryTypes.RefereeEmailDelivered]: ({ refReferee }) => ({
    icon: 'info',
    description: <p>Email delivered to {refReferee}.</p>,
  }),
  [CheckHistoryTypes.CandidateRefereeBackupRequest]: ({ refCandidate, refReferee }) => ({
    icon: 'info',
    description: <p>A backup request was initiated for referee {refReferee}.</p>,
  }),
  [CheckHistoryTypes.JobProcessingWithProviderId]: ({ entry, checkHuman, styles }) => ({
    icon: 'loader',
    description: (
      <p>
        {checkHuman && <span>{checkHuman} </span>}
        <span className={cn(styles.wrap)}>
          {entry?.message || 'application with provider for processing.'}
        </span>
      </p>
    ),
  }),
  [CheckHistoryTypes.CandidateDocumentRequestSent]: ({ refCandidate, checkHuman, entry }) => ({
    icon: 'info',
    description: (
      <p>{entry?.message || `Document request sent to ${refCandidate} for ${checkHuman}`}</p>
    ),
  }),
  [CheckHistoryTypes.CandidateDocumentUploaded]: ({ refCandidate, checkHuman, entry }) => ({
    icon: 'check',
    description: <p>{entry?.message || `${refCandidate} uploaded document for ${checkHuman}`}</p>,
  }),
  [CheckHistoryTypes.CheckBlocked]: ({ checkHuman, entry }) => ({
    icon: 'alert-circle',
    description: (
      <p>
        <b>{checkHuman}</b> blocked: {entry?.message || 'Candidate did not respond.'}
      </p>
    ),
  }),

  // Internal use only
  [CheckHistoryTypes.AdminGeneral]: createCustomAction('customNote'),
  [CheckHistoryTypes.AdminFollowUpProvider]: createCustomAction('customInfo'),
  [CheckHistoryTypes.AdminFollowUpCandidate]: createCustomAction('customInfo'),
  [CheckHistoryTypes.AdminFollowUpClient]: createCustomAction('customInfo'),
  [CheckHistoryTypes.AdminFeedback]: createCustomAction('customNote'),
};
